"use client"

import { useState } from "react"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"

interface AddEditAdminDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode?: "add" | "edit"
  initialData?: {
    branch: string
    permissions: string[]
  }
}

export function AddEditAdminDialog({ 
  open, 
  onOpenChange, 
  mode = "add",
  initialData 
}: AddEditAdminDialogProps) {
  const [selectedBranch, setSelectedBranch] = useState<string>(initialData?.branch || "")
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(initialData?.permissions || [])

  // Branch options
  const branches = [
    { id: "branch1", label: "สาขา 1" },
    { id: "branch2", label: "สาขา 2" },
  ]

  // Permission options
  const permissions = [
    { id: "dashboard-manage", label: "แดชบอร์ด/ร้านค้า" },
    { id: "branch", label: "บัญชี" },
    { id: "slip-management", label: "จัดการสลิป" },
    { id: "branch-select", label: "สาขา" },
    { id: "check-history", label: "เชื่อมต่อ" },
    { id: "api-connect", label: "API Connect" },
    { id: "camera-scan", label: "Camera Scan" },
    { id: "usage-history", label: "ประวัติการใช้งาน" },
    { id: "qr-receive", label: "QR รับเงิน" },
    { id: "partner-setting", label: "การตั้งค่าตรวจสลิป" },
    { id: "partner-management", label: "การตั้งค่าแพ็คเกจ" },
  ]

  // Handle branch selection toggle
  const toggleBranchSelection = (branchId: string) => {
    setSelectedBranch(prev => prev === branchId ? "" : branchId)
  }

  // Handle permission selection toggle
  const togglePermissionSelection = (permissionId: string) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    )
  }

  // Handle confirm button click
  const handleConfirm = () => {
    // TODO: Handle the form submission
    console.log("Mode:", mode)
    console.log("Selected branch:", selectedBranch)
    console.log("Selected permissions:", selectedPermissions)
    onOpenChange(false)
  }

  // Reset form when dialog opens/closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedBranch("")
      setSelectedPermissions([])
    } else if (initialData) {
      setSelectedBranch(initialData.branch)
      setSelectedPermissions(initialData.permissions)
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            เพิ่มแอดมิน
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {/* Branch Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-3">
              สาขา*
            </label>
            <div className="grid grid-cols-2 gap-4">
              {branches.map((branch) => (
                <label
                  key={branch.id}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedBranch === branch.id}
                    onCheckedChange={() => toggleBranchSelection(branch.id)}
                  />
                  <span className="text-sm text-gray-700">{branch.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Permissions Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-900 mb-3">
              สิทธิ์การเข้าถึง*
            </label>
            <div className="grid grid-cols-2 gap-4">
              {permissions.map((permission) => (
                <label
                  key={permission.id}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedPermissions.includes(permission.id)}
                    onCheckedChange={() => togglePermissionSelection(permission.id)}
                  />
                  <span className="text-sm text-gray-700">{permission.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="grid grid-cols-2 gap-3 px-6 py-4">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            ยกเลิก
          </Button>
          <Button
            onClick={handleConfirm}
            className="bg-brand-primary hover:bg-brand-primary/90 text-white"
          >
            สร้างลิงค์สมัคร
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
